import { sendRawRequest } from "@api/clients/axios";
import { useQuery } from "@tanstack/react-query";
import type { SelectedOption } from "@types_def/models/delivery.types";

export type TuseCalculateDeliveryPrice = {
  origin: string;
  destination: string;
  category: string;
  numberOfCars?: number;
  options: SelectedOption[];
  carType: "A" | "B" | "C";
};

type PriceResponse = {
  duration: {
    text: string;
    value: number;
  };
  distance: {
    text: string;
    value: number;
  };
  price: number | string;
  prices: {
    STANDARD: number | string;
    EXPRESS: number | string;
    OKM: number | string;
  };
};

export function useCalculateDeliveryPrice({
  origin,
  destination,
  category,
  numberOfCars,
  carType,
  options,
}: TuseCalculateDeliveryPrice) {
  return useQuery({
    queryKey: [origin, destination, category, numberOfCars, carType, options],
    queryFn: async () => {
      const data = await sendRawRequest<any, PriceResponse>(
        "post",
        "delivery/estimateMissionPrice",
        {
          origin,
          destination,
          numberOfCars,
          status: "PROFESSIONAL",
          carType,
        }
      );
      if (data.status === 201) {
        return {
          price: (
            Number(data.data.prices[category as keyof typeof data.data.prices]) +
            Number(
              options.reduce((acc, curr: SelectedOption) => {
                return acc + curr.price;
              }, 0)
            )
          ).toFixed(2),
          prices: data.data.prices,
          distance: data.data.distance,
          duration: data.data.duration,
        };
      }
    },
    enabled: !!origin && !!destination && !!category && !!numberOfCars && !!carType && !!options,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}
